import { Link } from '@cook-types/cook'
import { CookWidget } from '@widgets/types'
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import translate from '@i18n'
import styled, { css } from 'styled-components'
import { desktopCSS, mobileCSS } from '@measures/responsive'
import Blink from '@components/Blink'
import { ellipsis } from 'polished'
import ShowMoreButton from '@components/Buttons/ShowMoreButton'
import contextViewport, { ViewportTypeValues } from '@contexts/viewport'
import useTracking from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'
import usePageMetadata from '@hooks/usePageMetadata'

const componentMaxHeightTablet = 94

const { ViewportTypeContext, ViewportWidthContext } = contextViewport

interface LinkProps {
  text: string
  link: Link
}

interface TopThemenLinkProps {
  title?: string
  link: LinkProps
}

interface TopThemenProps {
  title: string
  links: LinkProps[]
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-wrap: wrap;
  border-bottom: 4px solid ${({ theme }) => theme.colors.grey.grey_20};
  margin: 20px 0;
`

const StyledTitle = styled.div`
  ${({
    theme: {
      fonts: { goodOT },
      colors: {
        grey: { grey_20 },
      },
    },
  }) => {
    return css`
      width: 100%;
      text-align: center;
      font-family: ${goodOT};
      font-size: '17px';
      font-weight: 900;
      line-height: 20.4px;
      text-transform: uppercase;
      background-color: ${grey_20};
      padding: 2px 0 1px 0;

      ${desktopCSS(css`
        width: calc(100% - 18px);
        text-align: initial;
        padding: 2px 0 1px 18px;
      `)}
    `
  }};
`

const StyledTopThemenLink = styled(Blink)`
  ${({
    theme: {
      fonts: { goodOT },
      colors: {
        grey: { grey_20, grey_80 },
      },
    },
  }) => {
    return css`
      border: 2px solid ${grey_20};
      border-radius: 20px;
      display: inline-block;
      padding: 2px 20px;
      margin: 20px 20px 0 0;
      font-family: ${goodOT};
      font-size: 17px;
      font-weight: 500;
      color: ${grey_80};
      text-decoration: none;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      ${ellipsis('300px', 1)}

      &:hover {
        background-color: ${grey_20};
      }
    `
  }};
`

interface LinksContainerProps {
  showMoreButton: boolean
  isMore: boolean
}

const LinksContainer = styled.div<LinksContainerProps>`
  ${({ showMoreButton, isMore }) => {
    return css`
      overflow: hidden;

      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      white-space: nowrap;
      text-overflow: ellipsis;

      min-width: 0;
      object-fit: contain;
      margin: 0 0 22px 0;

      ${mobileCSS(css`
        margin: 0 0 22px 8px;
      `)}

      ${showMoreButton && isMore && `height:${componentMaxHeightTablet}px;`}
    `
  }}
`

const StyledShowMoreButton = styled(ShowMoreButton)`
  margin-bottom: 22px;
`

const TopthemenLink: FunctionComponent<TopThemenLinkProps> = ({
  link,
  title,
}) => {
  const linkClickTrackingFn = useCallback(
    () => ({
      event: 'topthemen_click',
      eventAction: `${title}_click`,
      eventCategory: 'topThemen',
      eventLabel: link.text,
    }),
    [link.text, title]
  )

  const onLinkClick = useTracking(linkClickTrackingFn)

  return (
    <StyledTopThemenLink {...link.link} onClick={onLinkClick}>
      {link.text}
    </StyledTopThemenLink>
  )
}

const Topthemen: FunctionComponent<TopThemenProps> = ({ title, links }) => {
  const viewportType = useContext(ViewportTypeContext)
  const viewportWidth = useContext(ViewportWidthContext)
  const [showMoreButton, setShowMoreButton] = useState<boolean>(false)
  const [isDisplayedMore, setIsDisplayedMore] = useState<boolean>(true)
  const linksContainerElement = useRef<HTMLDivElement>(null)

  const { title: pageTitle } = usePageMetadata()

  useEffect(() => {
    hideOrShowLinks(viewportType)
  }, [viewportWidth, viewportType])

  const topthemenImpressionTrackingFn = useCallback(() => {
    return {
      event: 'topthemen_impression',
      eventAction: `${pageTitle}_impression`,
      eventCategory: 'topThemen',
      eventLabel: pageTitle,
    }
  }, [pageTitle])

  const handleImpressionTrack = useTracking(topthemenImpressionTrackingFn)

  const viewportRef = useViewportTracking({
    onImpression: handleImpressionTrack,
    track: true,
  })

  if (!links || links.length === 0) {
    return null
  }

  function hideOrShowLinks(viewportType: ViewportTypeValues) {
    // Scroll height includes content not visible due to overflow
    const scrollHeight = linksContainerElement.current?.scrollHeight

    const areLinksHidden = scrollHeight
      ? scrollHeight > componentMaxHeightTablet
      : false

    setShowMoreButton(
      areLinksHidden && viewportType !== ViewportTypeValues.DESKTOP
    )
  }

  return (
    <Container ref={viewportRef}>
      <StyledTitle>{title ?? translate('topStories.defaultTitle')}</StyledTitle>
      <LinksContainer
        ref={linksContainerElement}
        showMoreButton={showMoreButton}
        isMore={isDisplayedMore}>
        {links.map((link, index) => (
          <TopthemenLink
            key={link.link.href || index}
            link={link}
            title={pageTitle}
          />
        ))}
      </LinksContainer>
      {showMoreButton && (
        <StyledShowMoreButton
          isMore={isDisplayedMore}
          onClick={() =>
            setIsDisplayedMore((prevIsDisplayedMore) => !prevIsDisplayedMore)
          }
        />
      )}
    </Container>
  )
}

const widget: CookWidget = {
  kind: ['widget', 'topthemen'],
  component: Topthemen,
}

export default widget
